import React, {Component} from "react"
import PropTypes from "prop-types"

import id from "@irati/utils/id"
import {connect} from "@irati/store"
import ProfilePicture from "@irati/ui/src/ProfilePicture"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {injectIntl} from "@irati/utils/lang"
import tooltipLimiter from "@irati/recognition/utils/tooltipLimiter"

import Tooltip, {Tip} from "../shared/Tooltip"
import ProfilePicWithTrophy, {DEFAULT_TROPHY_SIZE} from "../ProfilePicWithTrophy"
import {calculateInnerWrapperSize} from "./helpers/position"
import {
  InnerWrapper,
  MultiplePicturesWrapper,
  SinglePictureWrapper,
  Placeholder
} from "./styled"

import {
  DEFAULT_OUTER_WRAPPER_SIZE,
  DEFAULT_INNER_WRAPPER_SIZE,
  DEFAULT_SINGLE_PICTURE_SIZE,
  DEFAULT_MULTIPLE_PICTURE_SIZE,
  DEFAULT_MAX_NUMBER_OF_USERS,
  EXPANDED_OFFSET_TWO_PROFILES,
  EXPANDED_OFFSET_THREE_PROFILES,
  OFFSET_TWO_PROFILES
} from "./constants"

class ProfilePicGroup extends Component {
  static propTypes = {
    children: PropTypes.node,
    users: PropTypes.array,
    intl: PropTypes.object.isRequired,
    outerWrapperSize: PropTypes.number,
    innerWrapperSize: PropTypes.number,
    singlePictureSize: PropTypes.number,
    multiplePictureSize: PropTypes.number,
    maxNumberOfUsers: PropTypes.number,
    isMobile: PropTypes.bool.isRequired,
    isExpanded: PropTypes.bool,
    trophy: PropTypes.object,
    trophySize: PropTypes.string
  }

  static defaultProps = {
    users: null,
    children: null,
    id: "profile_picture",
    outerWrapperSize: DEFAULT_OUTER_WRAPPER_SIZE,
    innerWrapperSize: DEFAULT_INNER_WRAPPER_SIZE,
    singlePictureSize: DEFAULT_SINGLE_PICTURE_SIZE,
    multiplePictureSize: DEFAULT_MULTIPLE_PICTURE_SIZE,
    maxNumberOfUsers: DEFAULT_MAX_NUMBER_OF_USERS,
    trophy: null,
    isExpanded: false,
    trophySize: DEFAULT_TROPHY_SIZE
  }

  getTooltip = (users) => {
    if (users.length === 0) { return null }
    const names = users.map((user) => ({
      uuid: user.uuid,
      name: firstnameLastname(user)
    }))

    return (
      <Tip id={id("tooltip", this.props)}>
        {tooltipLimiter(names, this.props.intl).map(({uuid, name}) =>
          <div key={uuid}>{name}</div>
        )}
      </Tip>
    )
  }

  getOffset = (index, total) => {
    if (index === 0) return null

    const {isMobile, isExpanded} = this.props

    if (isExpanded) {
      if (total === 2) {
        return EXPANDED_OFFSET_TWO_PROFILES
      } else if (total === 3) {
        return EXPANDED_OFFSET_THREE_PROFILES * index
      }
    } else if (!isMobile && index === 1 && total === 2) {
      return OFFSET_TWO_PROFILES
    }
    return null
  }

  render() {
    const {
      children,
      trophy,
      users,
      maxNumberOfUsers,
      outerWrapperSize,
      innerWrapperSize,
      singlePictureSize,
      multiplePictureSize,
      trophySize
    } = this.props

    if(!users) {
      return (
        <Placeholder />
      )
    }
    const hasSingleUser = users.length === 1
    const wrapperSize = calculateInnerWrapperSize(
      outerWrapperSize,
      innerWrapperSize,
      users.length
    )
    const usersToShow = users.slice(0, maxNumberOfUsers)
    const size = hasSingleUser
      ? singlePictureSize
      : multiplePictureSize
    const Wrapper = hasSingleUser
      ? SinglePictureWrapper
      : MultiplePicturesWrapper
    const Picture = (hasSingleUser && trophy)
      ? ProfilePicWithTrophy
      : ProfilePicture

    return (
      <Tooltip tip={this.getTooltip(users)} showMobile={false}>
        <InnerWrapper size={wrapperSize}>
          {usersToShow.map((user, index) => (
            <Wrapper
              key={user.uuid}
              index={index}
              total={usersToShow.length}
              wrapperSize={wrapperSize}
              singlePictureSize={singlePictureSize}
              multiplePictureSize={multiplePictureSize}
              offset={this.getOffset(index, usersToShow.length)}>
              <Picture
                user={user}
                size={size}
                trophy={trophy}
                trophySize={trophySize} />
            </Wrapper>
          ))}

          {usersToShow.length === 0 && children && (
            <Wrapper
              total={usersToShow.length}
              wrapperSize={wrapperSize}
              singlePictureSize={singlePictureSize}
              multiplePictureSize={multiplePictureSize}
              offset={0}>
              {children}
            </Wrapper>
          )}
        </InnerWrapper>
      </Tooltip>
    )
  }
}

const mapStateToProps = ({mq}) => {
  return {
    isMobile: mq.lessThan.md
  }
}

export default injectIntl(connect(mapStateToProps)(ProfilePicGroup))
